/**
 * useHousehold — Fetches and caches household data (name, members, settings, stores).
 * Uses the existing GET /v1/households/{householdId} endpoint.
 */

import { useState, useEffect, useRef, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { useApi } from './useApi';

interface HouseholdMember {
  userId: string;
  email: string;
  displayName: string;
  role: 'primary' | 'member';
  isAccountHolder: boolean;
  joinedAt: string;
}

interface HouseholdData {
  householdId: string;
  name: string;
  createdBy: string;
  members: HouseholdMember[];
  settings: Record<string, unknown> | null;
  stores: Record<string, unknown>[];
}

export function useHousehold() {
  const { user } = useAuth();
  const api = useApi();
  const [household, setHousehold] = useState<HouseholdData | null>(null);
  const [loading, setLoading] = useState(true);
  const fetchedRef = useRef<string | null>(null);

  useEffect(() => {
    if (!user?.householdId) {
      setLoading(false);
      return;
    }

    // Don't refetch if we already loaded this household
    if (fetchedRef.current === user.householdId) return;

    let cancelled = false;

    const fetchHousehold = async () => {
      try {
        const data = await api.get<HouseholdData>(`/v1/households/${user.householdId}`);
        if (!cancelled) {
          setHousehold(data);
          fetchedRef.current = user.householdId;
        }
      } catch (err) {
        console.error('Failed to fetch household:', err);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    fetchHousehold();
    return () => { cancelled = true; };
  // eslint-disable-next-line
  }, [user?.householdId]);

  const refetch = useCallback(async () => {
    if (!user?.householdId) return;
    try {
      const data = await api.get<HouseholdData>(`/v1/households/${user.householdId}`);
      setHousehold(data);
      fetchedRef.current = user.householdId;
    } catch (err) {
      console.error('Failed to fetch household:', err);
    }
  // eslint-disable-next-line
  }, [user?.householdId]);

  return { household, loading, refetch };
}